import { createSlice } from '@reduxjs/toolkit'
import {
  createOmsShipmentFulfilledAsync,
  updateOmsShipmentFulfilledAsync,
  updateOmsShipmentStatusAsync,
  deleteOmsShipmentAsync,
  getOmsShipmentListAsync,
  getOmsShippingCarrierFieldAsync,
} from '../actions/omsShippingActions'


interface OmsShippingState {
  loading: boolean
  saving: boolean
  detailLoading: boolean
  deleteLoading: boolean
  shipmentList: any[]
  shipmentListLoading: boolean
  error: string | null
  carrierFieldId: number | null
  carrierFieldLoading: boolean
  carrierFieldResolved: boolean
}

const initialState: OmsShippingState = {
  loading: false,
  saving: false,
  detailLoading: false,
  deleteLoading: false,
  shipmentList: [],
  shipmentListLoading: false,
  error: null,
  carrierFieldId: null,
  carrierFieldLoading: false,
  carrierFieldResolved: false,
}

const omsShippingSlice = createSlice({
  name: 'omsShipping',
  initialState,
  reducers: {
    clearOmsShippingError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // create shipment
    builder.addCase(createOmsShipmentFulfilledAsync.pending, (state) => {
      state.saving = true
      state.error = null
    })
    builder.addCase(createOmsShipmentFulfilledAsync.fulfilled, (state) => {
      state.saving = false
    })
    builder.addCase(createOmsShipmentFulfilledAsync.rejected, (state, action) => {
      state.saving = false
      state.error = action.error.message || 'Failed to create shipment'
    })

    // update shipment
    builder.addCase(updateOmsShipmentFulfilledAsync.pending, (state) => {
      state.saving = true
      state.error = null
    })
    builder.addCase(updateOmsShipmentFulfilledAsync.fulfilled, (state) => {
      state.saving = false
    })
    builder.addCase(updateOmsShipmentFulfilledAsync.rejected, (state, action) => {
      state.saving = false
      state.error = action.error.message || 'Failed to update shipment'
    })

    // update shipment status
    builder.addCase(updateOmsShipmentStatusAsync.pending, (state) => {
      state.saving = true
      state.error = null
    })
    builder.addCase(updateOmsShipmentStatusAsync.fulfilled, (state) => {
      state.saving = false
    })
    builder.addCase(updateOmsShipmentStatusAsync.rejected, (state, action) => {
      state.saving = false
      state.error = action.error.message || 'Failed to update shipment status'
    })

    // fetch shipment list
    builder.addCase(getOmsShipmentListAsync.pending, (state) => {
      state.shipmentListLoading = true
      state.detailLoading = true
      state.error = null
    })
    builder.addCase(getOmsShipmentListAsync.fulfilled, (state, action) => {
      state.shipmentListLoading = false
      state.detailLoading = false
      state.shipmentList = Array.isArray(action.payload?.data) ? action.payload.data : []
    })
    builder.addCase(getOmsShipmentListAsync.rejected, (state, action) => {
      state.shipmentListLoading = false
      state.detailLoading = false
      state.error = action.error.message || 'Failed to fetch shipment details'
    })

    // delete shipment
    builder.addCase(deleteOmsShipmentAsync.pending, (state) => {
      state.deleteLoading = true
      state.error = null
    })
    builder.addCase(deleteOmsShipmentAsync.fulfilled, (state) => {
      state.deleteLoading = false
    })
    builder.addCase(deleteOmsShipmentAsync.rejected, (state, action) => {
      state.deleteLoading = false
      state.error = action.error.message || 'Failed to delete shipment'
    })

    // fetch carrier field ID
    builder.addCase(getOmsShippingCarrierFieldAsync.pending, (state) => {
      state.carrierFieldLoading = true
    })
    builder.addCase(getOmsShippingCarrierFieldAsync.fulfilled, (state, action) => {
      state.carrierFieldLoading = false
      state.carrierFieldResolved = true
      // Extract the SHIPPINGPROVIDER field ID from the response array
      let fields: any[] = []
      if (Array.isArray(action.payload?.results)) fields = action.payload.results
      else if (Array.isArray(action.payload?.data)) fields = action.payload.data
      else if (Array.isArray(action.payload)) fields = action.payload
      else if (Array.isArray(action.payload?.data?.results)) fields = action.payload.data.results

      const shippingField = fields.find((f: any) => f.api_code === 'SHIPPINGPROVIDER')
      state.carrierFieldId = shippingField?.id ?? null
    })
    builder.addCase(getOmsShippingCarrierFieldAsync.rejected, (state) => {
      state.carrierFieldLoading = false
      state.carrierFieldResolved = true
    })
  },
})

export const { clearOmsShippingError } = omsShippingSlice.actions
export default omsShippingSlice.reducer
